import { FC, useEffect } from 'react';
import {
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
	createSupportedPostalOperatorRequest,
	resetSaveSupportedPostalOperatorSuccess,
	updateSupportedPostalOperatorRequest,
} from 'redux/reducers/supportedPostalOperators/reducer';
import {
	getSupportedPostalOperatorSaveLoading,
	getSupportedPostalOperatorSaveSuccess,
} from 'redux/reducers/supportedPostalOperators/selectors';
import { getCountryListData } from 'redux/reducers/country/selectors';
import { TSupportedPostalOperatorDialogProps, TSupportedPostalOperatorFormValues } from './types';

const validationSchema = Yup.object({
	name: Yup.string().required('Name is required').max(255, 'Max 255 characters'),
	countryId: Yup.number().required('Country is required').min(1, 'Country is required'),
	upuCode: Yup.string().max(50, 'Max 50 characters'),
});

export const SupportedPostalOperatorDialog: FC<TSupportedPostalOperatorDialogProps> = ({
	open,
	onClose,
	operatorToEdit,
}) => {
	const dispatch = useDispatch();
	const isEdit = !!operatorToEdit;

	const loading = useSelector(getSupportedPostalOperatorSaveLoading);
	const saveSuccess = useSelector(getSupportedPostalOperatorSaveSuccess);
	const countries = useSelector(getCountryListData);

	const formik = useFormik<TSupportedPostalOperatorFormValues>({
		initialValues: {
			name: operatorToEdit?.name ?? '',
			countryId: operatorToEdit?.countryId ?? 0,
			upuCode: operatorToEdit?.upuCode ?? '',
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			const countryCode =
				countries?.find((country) => country.id === values.countryId)?.isoAlpha2 ?? null;
			const body = {
				name: values.name,
				countryId: Number(values.countryId),
				countryCode,
				upuCode: values.upuCode || null,
			};
			if (isEdit && operatorToEdit) {
				dispatch(updateSupportedPostalOperatorRequest({ id: operatorToEdit.id, body }));
			} else {
				dispatch(createSupportedPostalOperatorRequest(body));
			}
		},
	});

	useEffect(() => {
		if (saveSuccess) {
			dispatch(resetSaveSupportedPostalOperatorSuccess());
			formik.resetForm();
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saveSuccess]);

	const countryOptions = countries ?? [];
	const selectedCountry = countryOptions.find((country) => country.id === formik.values.countryId);

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				{isEdit ? 'Edit Supported Postal Operator' : 'Add New Supported Postal Operator'}
			</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<TextField
						label="Name"
						name="name"
						size="small"
						value={formik.values.name}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.name && !!formik.errors.name}
						helperText={formik.touched.name && formik.errors.name}
					/>
					<Autocomplete
						options={countryOptions}
						value={selectedCountry ?? null}
						onChange={(event, country) => formik.setFieldValue('countryId', country?.id ?? 0)}
						getOptionLabel={(country) => country.name}
						isOptionEqualToValue={(country, selected) => country.id === selected.id}
						size="small"
						renderInput={(params) => (
							<TextField
								{...params}
								label="Country"
								name="countryId"
								onBlur={formik.handleBlur}
								error={formik.touched.countryId && !!formik.errors.countryId}
								helperText={formik.touched.countryId && formik.errors.countryId}
							/>
						)}
					/>
					<TextField
						label="UPU Code"
						name="upuCode"
						size="small"
						value={formik.values.upuCode}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.upuCode && !!formik.errors.upuCode}
						helperText={formik.touched.upuCode && formik.errors.upuCode}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					{isEdit ? 'Save' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
